<template>
  <div class="container-fluid px-4 mt-4">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <h5 class="fw-bold mb-0">공지사항 관리</h5>
      <button class="btn btn-primary btn-sm" @click="openWriteModal">+ 공지사항 작성</button>
    </div>

    <!-- 로딩 -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">조회 중...</span>
      </div>
      <p class="mt-3 text-muted">공지사항을 로드 중입니다...</p>
    </div>

    <!-- 오류 -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      <h5 class="alert-heading">오류 발생</h5>
      <p class="mb-0">{{ error }}</p>
    </div>

    <!-- 공지사항 목록 -->
    <div v-else>
      <div v-if="notices.length === 0" class="alert alert-info">
        <p class="mb-0">공지사항이 없습니다.</p>
      </div>

      <div v-else class="table-responsive">
        <table class="table table-sm">
          <thead>
            <tr>
              <th>#</th>
              <th>제목</th>
              <th class="text-center">작성일</th>
              <th class="text-center" style="width: 150px;">관리</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="notice in notices" :key="notice.NoticeSeq">
              <td class="text-muted small">{{ notice.NoticeSeq }}</td>
              <td>{{ notice.Title }}</td>
              <td class="text-center text-muted small">{{ formatDate(notice.CreatedDate) }}</td>
              <td class="text-center">
                <button class="btn btn-sm btn-outline-primary me-1" @click="openEditModal(notice)">수정</button>
                <button class="btn btn-sm btn-outline-danger" @click="deleteNotice(notice.NoticeSeq)">삭제</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 공지사항 작성/수정 모달 -->
    <div v-if="noticeModal" class="modal-backdrop-custom">
      <div class="modal-box" @click.stop>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="fw-bold mb-0">{{ isEditMode ? '공지사항 수정' : '공지사항 작성' }}</h6>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>

        <!-- 제목 -->
        <div class="mb-3">
          <label class="form-label form-label-sm">제목 <span class="text-danger">*</span></label>
          <input
            v-model="noticeForm.Title"
            type="text"
            class="form-control form-control-sm"
            placeholder="제목을 입력하세요"
            maxlength="200"
          />
          <small class="form-text text-muted">{{ noticeForm.Title.length }} / 200</small>
        </div>

        <!-- 내용 -->
        <div class="mb-3">
          <label class="form-label form-label-sm">내용 <span class="text-danger">*</span></label>
          <textarea
            v-model="noticeForm.Content"
            class="form-control form-control-sm"
            rows="10"
            placeholder="내용을 입력하세요"
          ></textarea>
        </div>

        <div class="d-flex gap-2 justify-content-end">
          <button type="button" class="btn btn-secondary btn-sm" @click="closeModal">취소</button>
          <button type="button" class="btn btn-primary btn-sm" @click="saveNotice">
            {{ isEditMode ? '수정' : '저장' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'AdminNoticeView',
  data() {
    return {
      loading: false,
      error: null,
      notices: [],
      noticeModal: false,
      isEditMode: false,
      noticeForm: {
        NoticeSeq: null,
        Title: '',
        Content: ''
      }
    }
  },
  mounted() {
    this.fetchNotices()
  },
  methods: {
    async fetchNotices() {
      this.loading = true
      this.error = null
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/admin/notices`)
        if (res.data.success && res.data.notices) {
          this.notices = res.data.notices
        } else {
          this.error = '공지사항을 불러올 수 없습니다.'
        }
      } catch (e) {
        console.error('fetchNotices error:', e)
        this.error = e.response?.data?.message || '공지사항 목록을 조회할 수 없습니다.'
      } finally {
        this.loading = false
      }
    },
    openWriteModal() {
      this.isEditMode = false
      this.noticeForm = {
        NoticeSeq: null,
        Title: '',
        Content: ''
      }
      this.noticeModal = true
    },
    openEditModal(notice) {
      this.isEditMode = true
      this.noticeForm = {
        NoticeSeq: notice.NoticeSeq,
        Title: notice.Title,
        Content: notice.Content
      }
      this.noticeModal = true
    },
    closeModal() {
      this.noticeModal = false
      this.noticeForm = {
        NoticeSeq: null,
        Title: '',
        Content: ''
      }
    },
    async saveNotice() {
      // 유효성 검사
      if (!this.noticeForm.Title.trim()) {
        alert('제목을 입력해주세요.')
        return
      }
      if (!this.noticeForm.Content.trim()) {
        alert('내용을 입력해주세요.')
        return
      }

      try {
        let res
        if (this.isEditMode) {
          // 수정
          res = await axios.put(
            `${process.env.VUE_APP_API_BASE}/api/admin/notices/${this.noticeForm.NoticeSeq}`,
            {
              Title: this.noticeForm.Title,
              Content: this.noticeForm.Content
            }
          )
        } else {
          // 등록
          res = await axios.post(
            `${process.env.VUE_APP_API_BASE}/api/admin/notices`,
            {
              Title: this.noticeForm.Title,
              Content: this.noticeForm.Content
            }
          )
        }

        if (res.data.success) {
          alert(res.data.message)
          this.closeModal()
          this.fetchNotices()
        }
      } catch (e) {
        console.error('saveNotice error:', e)
        alert(e.response?.data?.message || '저장에 실패했습니다.')
      }
    },
    async deleteNotice(noticeSeq) {
      if (!confirm('정말 삭제하시겠습니까?')) {
        return
      }

      try {
        const res = await axios.delete(
          `${process.env.VUE_APP_API_BASE}/api/admin/notices/${noticeSeq}`
        )

        if (res.data.success) {
          alert(res.data.message)
          this.fetchNotices()
        }
      } catch (e) {
        console.error('deleteNotice error:', e)
        alert(e.response?.data?.message || '삭제에 실패했습니다.')
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
  }
}
</script>

<style scoped>
/* 모달 스타일 */
.modal-backdrop-custom {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-box {
  background: white;
  border-radius: 6px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 1.5rem;
}

.form-label-sm {
  font-size: 0.875rem;
  color: #333;
}

.form-control-sm {
  font-size: 0.875rem;
}

textarea.form-control-sm {
  font-family: inherit;
  resize: vertical;
}

.table-responsive {
  border-radius: 6px;
}

.table {
  margin-bottom: 0;
}

.table thead th {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  font-weight: 600;
  color: #495057;
  font-size: 0.875rem;
  padding: 0.75rem;
}

.table tbody td {
  padding: 0.75rem;
  border-bottom: 1px solid #dee2e6;
  vertical-align: middle;
}

.table tbody tr:hover {
  background-color: #f8f9fa;
}
</style>
